import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { slugifyCountry, useCountries } from '../context/CountryContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const WHY_ICONS = ['school', 'description', 'work', 'public'];

export default function WhyChooseCountryPage() {
  const { slug } = useParams();
  const { countries } = useCountries();
  const country = countries.find((item) => slugifyCountry(item.country) === slug)?.country || slug.replace(/-/g, ' ');
  const [guide, setGuide] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/country-guides/${encodeURIComponent(country)}`).then((r) => r.json()),
      fetch(`${API_BASE}/institutions?country=${encodeURIComponent(country)}`).then((r) => r.json()),
    ])
      .then(([guideJson, instJson]) => {
        setGuide(guideJson.success ? guideJson.data : null);
        setInstitutions(instJson.success ? instJson.data : []);
      })
      .finally(() => setLoading(false));
  }, [country]);

  if (loading) return <p className="text-center py-40 text-[#64748b]">Loading...</p>;
  if (!guide) return <p className="text-center py-40 text-[#64748b]">Country guide not available yet.</p>;

  const livingCost = guide.livingCostSummary || {};

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative h-[600px] flex items-center overflow-hidden">
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${guide.heroImageUrl || ''})` }}
          />
          <div
            className="absolute inset-0 z-10 opacity-70"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          />
          <div className="relative z-20 max-w-[1280px] mx-auto px-6 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 mb-6">
                <span className="text-2xl">🌍</span>
                <span className="text-white text-sm tracking-wider uppercase">Premium Destination</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                {guide.whyChooseHeadline || `Why Choose ${country}?`}
              </h1>
              <div className="flex flex-wrap gap-4">
                <a href="#benefits" className="bg-white text-[#00255a] px-8 py-4 rounded-xl font-bold shadow-lg hover:scale-105 transition-all">
                  Explore Benefits
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Points */}
        <section className="py-24 max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(guide.whyChoosePoints || []).map((point, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-[#e2e8f0] shadow-sm hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-[#d8e2ff]/50 rounded-xl flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-[#00255a] text-3xl">{WHY_ICONS[i % WHY_ICONS.length]}</span>
                </div>
                <p className="text-[#44474f] font-medium">{point}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-[#f8fafc] py-24" id="benefits">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[#00255a] mb-4">{guide.benefitsHeadline || 'What You Gain'}</h2>
              <p className="text-[#64748b] max-w-2xl mx-auto">Benefits after completing your program in {country}.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(guide.benefitsPoints || []).map((point, i) => (
                <div key={i} className="flex items-center gap-4 bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-sm">
                  <span className="material-symbols-outlined text-[#ff7b7b] text-3xl">check_circle</span>
                  <span className="text-lg font-semibold text-[#00255a]">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cost of Living */}
        {Object.keys(livingCost).length > 0 && (
          <section className="py-24 max-w-[1280px] mx-auto px-6">
            <div className="bg-white p-12 rounded-[32px] border border-[#e2e8f0] shadow-sm max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-[#00255a]">Cost of Living</h2>
                <p className="text-[#64748b]">Estimated monthly expenses in {country}</p>
              </div>
              <div className="flex flex-col md:flex-row gap-8 items-center">
                {Object.entries(livingCost).map(([key, value]) => (
                  <div key={key} className="flex-1 flex flex-col items-center p-8 bg-[#f2f4f6] rounded-2xl w-full">
                    <span className="material-symbols-outlined text-[#365da3] text-5xl mb-4">
                      {key.toLowerCase().includes('food') ? 'restaurant' : 'apartment'}
                    </span>
                    <span className="text-xs text-[#64748b] uppercase mb-2">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-2xl font-bold text-[#00255a] text-center">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Tourist / Beautiful Places */}
        {guide.touristPlaces?.length > 0 && (
          <section className="py-24 bg-[#f8fafc]">
            <div className="max-w-[1280px] mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-[#00255a]">
                  {guide.touristPlacesHeadline || `Places to Explore in ${country}`}
                </h2>
                <p className="text-[#64748b] max-w-2xl mx-auto mt-2">
                  Beyond your studies or career, {country} has plenty worth discovering.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {guide.touristPlaces.map((place, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden border border-[#e2e8f0] shadow-sm hover:shadow-lg transition-all">
                    <div className="h-56 bg-[#eceef0] relative">
                      {place.imageUrl && (
                        <img className="w-full h-full object-cover" src={place.imageUrl} alt={place.name} />
                      )}
                    </div>
                    <div className="p-6">
                      <h4 className="text-lg font-bold text-[#00255a] mb-2">{place.name}</h4>
                      <p className="text-[#64748b] text-sm">{place.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Affiliated Institutions */}
        {institutions.length > 0 && (
          <section className="py-24 max-w-[1280px] mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[#00255a]">Affiliated Institutions in {country}</h2>
              <p className="text-[#64748b]">Connect with our partner universities and employers</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {institutions.map((inst) => (
                <Link
                  key={inst.id}
                  to={`/openings?country=${encodeURIComponent(country)}&institutionId=${inst.id}`}
                  className="group bg-white p-8 rounded-2xl border border-[#e2e8f0] shadow-sm hover:shadow-lg transition-all flex flex-col items-center text-center"
                >
                  <div className="w-20 h-20 mb-6 flex items-center justify-center bg-[#f7f9fb] rounded-xl p-4">
                    {inst.logoUrl ? (
                      <img className="w-full h-full object-contain" src={inst.logoUrl} alt={inst.name} />
                    ) : (
                      <span className="material-symbols-outlined text-3xl text-[#00255a]">account_balance</span>
                    )}
                  </div>
                  <h4 className="text-lg font-bold text-[#00255a] mb-3">{inst.name}</h4>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#d8e2ff]/40 rounded-full">
                    <span className="text-xs">🌍</span>
                    <span className="text-xs text-[#00255a]">Partner Institution</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-[1280px] mx-auto px-6 pb-24">
          <div
            className="rounded-[40px] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-white mb-8">Ready to start your journey to {country}?</h2>
              <p className="text-white/80 mb-10 max-w-2xl mx-auto">
                Our specialized consultants are here to guide you through every step, from application to arrival.
              </p>
              <Link
                to={`/openings?country=${encodeURIComponent(country)}`}
                className="inline-block bg-white text-[#00255a] px-10 py-5 rounded-2xl font-bold shadow-xl hover:scale-105 transition-all"
              >
                View Open Programs
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
